import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import "./AddItem.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const CATEGORIES = ["Furniture", "Clothes", "Electronics", "Kitchen", "Books", "Toys", "Sports", "Other"];
const CONDITIONS = ["New", "Excellent", "Good", "Fair", "Worn"];

function EditItem({ toggleDarkMode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = localStorage.getItem("name");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [previewImages, setPreviewImages] = useState([]);

  const [form, setForm] = useState({
    title: "",
    category: "",
    condition: "",
    description: "",
    location: "",
    originalValue: "",
    tags: "",
    ecoDelivery: true,
    images: []
  });

  useEffect(() => {
    fetch(`${API}/items/${id}`)
      .then(r => r.json())
      .then(data => {
        // Redirect if not owner
        if (data.owner !== currentUser) {
          navigate("/items");
          return;
        }
        setForm({
          title: data.title || "",
          category: data.category || "",
          condition: data.condition
            ? data.condition.charAt(0).toUpperCase() + data.condition.slice(1)
            : "",
          description: data.description || "",
          location: data.location || "",
          originalValue: data.originalValue || "",
          tags: Array.isArray(data.tags) ? data.tags.join(", ") : "",
          ecoDelivery: data.ecoDelivery ?? true,
          images: data.images || []
        });
        setPreviewImages(data.images || []);
        setLoading(false);
      })
      .catch(() => navigate("/items"));
  }, [id, currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const readers = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = ev => resolve(ev.target.result);
      reader.readAsDataURL(file);
    }));
    Promise.all(readers).then(results => {
      setPreviewImages(results);
      setForm(f => ({ ...f, images: results }));
    });
  };

  const handleRemoveImage = (index) => {
    const updated = previewImages.filter((_, i) => i !== index);
    setPreviewImages(updated);
    setForm(f => ({ ...f, images: updated }));
  };

  const showMsg = (text, type) => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 3500);
  };

  const handleSave = async () => {
    if (!form.title || !form.category || !form.condition || !form.description) {
      return showMsg("Please fill in all required fields", "error");
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/items/${id}?owner=${encodeURIComponent(currentUser)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: form.title,
          category: form.category,
          condition: form.condition.toLowerCase(),
          description: form.description,
          location: form.location,
          originalValue: form.originalValue,
          tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
          images: form.images,
          ecoDelivery: form.ecoDelivery
        })
      });
      const data = await res.json();
      if (res.ok) {
        showMsg("Item updated successfully!", "success");
        setTimeout(() => navigate(`/items/${id}`), 1200);
      } else {
        showMsg(data.message || "Update failed", "error");
      }
    } catch {
      showMsg("Server error. Try again.", "error");
    }
    setSaving(false);
  };

  if (loading) return (
    <DashboardLayout toggleDarkMode={toggleDarkMode}>
      <div style={{ display: "flex", justifyContent: "center", padding: "80px" }}>
        <div className="lazy-spinner" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout toggleDarkMode={toggleDarkMode}>
      <div className="additem-page">

        {/* TITLE ROW */}
        <div className="additem-title-row">
          <button className="additem-back" onClick={() => navigate(`/items/${id}`)}>
            ← Back
          </button>
          <div>
            <h1 className="additem-heading">✏️ Edit Item</h1>
            <p className="additem-subheading">Update your item details below</p>
          </div>
        </div>

        {/* MESSAGE */}
        {msg.text && (
          <div style={{
            padding: "12px 16px",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: "500",
            marginBottom: "8px",
            background: msg.type === "success" ? "#f0fdf4" : "#fef2f2",
            color: msg.type === "success" ? "#15803d" : "#dc2626",
            border: `1px solid ${msg.type === "success" ? "#bbf7d0" : "#fecaca"}`
          }}>
            {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
          </div>
        )}

        <div className="additem-layout">
          <div className="additem-left">

            {/* FORM CARD */}
            <div className="additem-card">
              <h2 className="additem-card-title">✏️ Edit Item Details</h2>

              <div className="additem-field">
                <label>Item Name <span className="required">*</span></label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g. iPhone 12, Winter Jacket"
                />
              </div>

              <div className="additem-row">
                <div className="additem-field">
                  <label>Category <span className="required">*</span></label>
                  <select name="category" value={form.category} onChange={handleChange}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="additem-field">
                  <label>Condition <span className="required">*</span></label>
                  <select name="condition" value={form.condition} onChange={handleChange}>
                    <option value="">Select condition</option>
                    {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="additem-field">
                <label>Description <span className="required">*</span></label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Describe the item's condition, features and any defects"
                />
              </div>

              <div className="additem-row">
                <div className="additem-field">
                  <label>Current Location</label>
                  <input
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="e.g. Bedroom, Garage"
                  />
                </div>
                <div className="additem-field">
                  <label>Original Value <span className="optional">(Optional)</span></label>
                  <input
                    name="originalValue"
                    value={form.originalValue}
                    onChange={handleChange}
                    placeholder="e.g. $50, $200"
                  />
                </div>
              </div>

              <div className="additem-field">
                <label>Tags <span className="optional">(Optional)</span></label>
                <input
                  name="tags"
                  value={form.tags}
                  onChange={handleChange}
                  placeholder="e.g. vintage, brand new (comma separated)"
                />
              </div>

              <div className="additem-checks">
                <label className="additem-check">
                  <input
                    type="checkbox"
                    name="ecoDelivery"
                    checked={form.ecoDelivery}
                    onChange={handleChange}
                  />
                  Available for eco-delivery service
                </label>
              </div>

              <button
                className="additem-submit"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "💾 Save Changes"}
              </button>
            </div>

            {/* PHOTO CARD */}
            <div className="additem-card">
              <h2 className="additem-card-title">📷 Update Photos</h2>

              {previewImages.length > 0 && (
                <div className="additem-preview-grid" style={{ marginBottom: "16px" }}>
                  {previewImages.map((src, i) => (
                    <div key={i} style={{ position: "relative", display: "inline-block" }}>
                      <img src={src} alt={`preview-${i}`} />
                      <button
                        onClick={() => handleRemoveImage(i)}
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "4px",
                          background: "rgba(0,0,0,0.6)",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: "22px",
                          height: "22px",
                          cursor: "pointer",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}

              <label className="additem-upload">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
                <span className="upload-icon">📷</span>
                <p>{previewImages.length > 0 ? "Click to replace photos" : "Click to add photos"}</p>
                <p className="upload-sub">Adding new photos will replace existing ones</p>
              </label>
            </div>

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default EditItem;